// Owns the matrix-family block state: which view its sub-questions are charted in and what that
// view reads, whether the chart or the word cloud is on screen and, when it is the chart, its mode
// and zoom, the sort order of the list below, and which batches are open. Seeded from the
// question's params and re-seeded whenever any of them changes outside the block.
//
// The block answers for its whole subtree rather than for itself alone: every batch is a
// report-param owner in its own right, and each hands its api up as it mounts. So the chart-mode
// normalization, the preset bag and the export payload are gathered here, across this block and
// every batch under it, and the shell only forwards what it is given.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import {
  calcLabelClickTarget,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType
} from '@aytm/app/stats/page/components/lib/utils'
import { getDefaultChartMode } from '@aytm/app/stats/page/question-views/mx/components/chart/lib/chartModes'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/mx/lib/display-range'
import { calcBatches } from '@aytm/app/stats/page/question-views/mx/lib/props/batches'
import { calcOpenKeys } from '@aytm/app/stats/page/question-views/mx/lib/open-batches'
import {
  calcChartParamsSection,
  calcCollapseSection,
  calcModelChartMode,
  calcSortParamsSection,
  calcViewModeReset,
  calcViewSection,
  calcZoomSection,
  isWcChartMode,
  resolveChartMode,
  resolveSortOrder,
  resolveUncollapsed,
  resolveViewState,
  WC_CHART_MODE
} from '@aytm/app/stats/page/question-views/mx/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export default function useMatrixState({
  params,
  paramsRevision,
  questionKey,
  type,
  subType,
  format,
  answer,
  batches,
  degree,
  viewModes,
  topsModes,
  defaultSortOrder,
  labelTarget,
  onReady,
  onStateChange,
  onBatchParamsChange,
  onBatchWcParamsChange,
  onBatchReady: announceBatchReady
}) {
  const context = { key: questionKey, type, subType, format, answer, batches, degree }

  const contextRef = useRef()
  contextRef.current = { ...context, viewModes, topsModes, defaultSortOrder }

  const seed = useCallback(source => {
    const { format, viewModes, topsModes, type, answer, defaultSortOrder } = contextRef.current

    const view = resolveViewState(source, { viewModes, topsModes, type, total: answer?.total || 0 })

    return {
      viewMode: view.viewMode,
      labelsMode: view.labelsMode,
      topsMode: view.topsMode,
      uncollapsed: resolveUncollapsed(source.cb),
      // a stored view the question no longer offers is rewritten, and starts the rest over with it
      ...(view.error
        ? calcViewModeReset(view.viewMode, defaultSortOrder)
        : {
            chartMode: resolveChartMode(source, format),
            sortOrder: resolveSortOrder(source.s, defaultSortOrder),
            zoomLevel: decodeZoomParam(source.z)
          })
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  // a view the question does not offer is announced as a change, so the stored one is rewritten
  const seedNeedsWrite = useRef(false)

  const [state, setState] = useState(() => {
    const source = params || {}
    const { viewModes, topsModes, type, answer } = contextRef.current

    seedNeedsWrite.current = resolveViewState(source, {
      viewModes,
      topsModes,
      type,
      total: answer?.total || 0
    }).error

    return seed(source)
  })

  const snapshot = useRef()
  snapshot.current = { state, context, labelTarget }

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  // the block's cloud, when it is showing one, hands its api up the way any block does
  const wcApiRef = useRef(null)
  const onWcReady = useCallback(api => {
    wcApiRef.current = api
  }, [])

  const announceRefs = useRef()
  announceRefs.current = { onBatchParamsChange, onBatchWcParamsChange, announceBatchReady }

  // Every batch hands its api up as it mounts; the block answers for them as well as for itself, so
  // the exporters reach the whole subtree through one call. The shell is told too -- the rest of
  // the page still walks it as a list of sub-item views.
  const batchApis = useRef({})
  const onBatchReady = useCallback((position, batchApi, meta) => {
    if (null == batchApi) delete batchApis.current[position]
    else batchApis.current[position] = { api: batchApi, meta: meta || {} }

    announceRefs.current.announceBatchReady?.(position, batchApi, meta)
  }, [])

  // A batch announces its own changes the way any block does; this block is what writes them down,
  // so the announcement is forwarded with the key the params hang off.
  const onBatchStateChange = useCallback((position, key, reason) => {
    if ('state' !== reason) return

    const entry = batchApis.current[position]
    if (!entry) return

    announceRefs.current.onBatchParamsChange?.(key, entry.api.getReportParamsSection?.() || {})
  }, [])

  const onBatchWcParams = useCallback((id, params) => announceRefs.current.onBatchWcParamsChange?.(id, params), [])

  // every batch, paired with whatever it answers - the ones that answer nothing drop out
  const gather = useCallback(
    read =>
      Object.entries(batchApis.current).reduce((acc, [position, entry]) => {
        const value = read(entry.api, position, entry.meta)

        return null == value ? acc : acc.concat([[position, value]])
      }, []),
    []
  )

  const pendingReason = useRef(seedNeedsWrite.current ? 'state' : 'seed')

  const onModelChange = useCallback((change = {}) => {
    setState(current => {
      // picking a view starts the chart mode, the sort and the zoom over with it
      const next = Object.hasOwn(change, 'viewMode')
        ? { ...calcViewModeReset(change.viewMode, contextRef.current.defaultSortOrder), ...change }
        : change

      if (Object.entries(next).every(([key, value]) => current[key] === value)) return current

      pendingReason.current = 'state'

      return { ...current, ...next }
    })
  }, [])

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by, as opposed to `chartMode`, which is null until the researcher picks one
    const resolveExportChartMode = () => {
      const { state, context } = snapshot.current

      return getDefaultChartMode(
        state.chartMode || calcModelChartMode(state.chartMode, context.format),
        state.viewMode,
        context.answer,
        calcIsRType(context) ? getModelValue('rType') : 'cases',
        getModelValue('chartMode'),
        true === context.format?.percentsOnly
      )
    }

    // the axis the chart is showing. The rendered chart knows it, zoom included; the maths is the
    // fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { state, context } = snapshot.current
      if (getTrait() && calcIsTraitType(context)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        viewMode: state.viewMode,
        topsMode: state.topsMode,
        zoomLevel: zoomLevel || state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        format: context.format,
        answer: context.answer,
        batches: context.batches,
        degree: context.degree,
        rType: calcIsRType(context) ? getModelValue('rType') : 'cases',
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current
        const { viewModes, defaultSortOrder } = contextRef.current

        return {
          ...calcViewSection({ ...state, viewModes, type: context.type, total: context.answer?.total || 0 }),
          ...calcZoomSection(state.zoomLevel, getModelValue('zoomLevel')),
          ...calcSortParamsSection(state.sortOrder, defaultSortOrder),
          ...calcCollapseSection(state.uncollapsed),
          // the pie rule drops the zoom with the axis, so the chart section is applied over it
          ...calcChartParamsSection({
            chartMode: state.chartMode,
            format: context.format,
            viewMode: state.viewMode,
            answer: context.answer,
            rType: calcIsRType(context) ? getModelValue('rType') : 'cases',
            globalChartMode: getModelValue('chartMode')
          })
        }
      },
      // pptx / summary bridge: how the question is being read, with the open batches spelled out --
      // the exporters read `items` off this
      getExportData: () => {
        const { state, context } = snapshot.current

        const batches = calcBatches(context.batches)
        const open = new Set(calcOpenKeys({ batches, uncollapsed: state.uncollapsed }))
        const chartModeResolved = resolveExportChartMode()

        return {
          viewMode: state.viewMode,
          labelsMode: state.labelsMode,
          topsMode: state.topsMode,
          chartMode: state.chartMode,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          sortOrder: state.sortOrder,
          uncollapsed: state.uncollapsed,
          items: batches
            .filter(batch => open.has(String(batch.position)))
            .reduce((acc, batch) => {
              const entry = batchApis.current[batch.position]
              if (!entry) return acc

              return acc.concat([
                {
                  uid: batch.uid || batch.position,
                  position: batch.position,
                  model: { ...batch, ...(entry.api.getExportData?.() || {}) }
                }
              ])
            }, [])
        }
      },
      getDisplayRange: resolveDisplayRange,
      // preset bridge: the param every cloud under this block would end up with if the preset were
      // poured into it -- its own and every batch's. Resolving commits nothing; the shell writes
      // the merged section down.
      getWCParam: preset => {
        // the api outlives the cloud being taken off screen, so what is showing decides, not the ref
        const wcApi = isWcChartMode(snapshot.current.state.chartMode) ? wcApiRef.current : null
        const own = wcApi ? wcApi.getWcParamsSection?.(wcApi.importWcPreset?.(preset)) : null

        return gather(batchApi => batchApi.getWCParam?.(preset)).reduce(
          (acc, [, section]) => Object.assign(acc, section),
          { ...own }
        )
      },
      // The answer a clicked row stands for, addressed through the batch it belongs to -- a row of a
      // sub-question filters by that sub-question, not by the matrix the list hangs off.
      getLabelClickTarget: (position, batchPosition, answerVariants) => {
        const { context, labelTarget } = snapshot.current
        const batch = (context.batches || []).find(item => item && item.position == batchPosition)

        return calcLabelClickTarget({
          ...labelTarget,
          // the position alone scopes the key; a miss must not drop back to the question the list
          // hangs off
          batch: batch || (null == batchPosition ? undefined : { position: batchPosition }),
          // a sentiments or tags row names its own variant, and only that list knows the tag
          answerVariants: answerVariants?.length ? answerVariants : batch?.answerVariants || [],
          position
        })
      },
      update: onModelChange
    }
  }, [gather, onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  //
  // Keyed on the revision the shell bumps per host render, not on the contents of the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the contents missed an undo restoring a value
  // the block had already moved away from locally, because the block's own write is silent and the
  // string it was handed had never changed.
  useEffect(() => {
    const source = params ?? {}
    const { viewModes, topsModes, type, answer } = contextRef.current

    pendingReason.current = resolveViewState(source, { viewModes, topsModes, type, total: answer?.total || 0 }).error
      ? 'state'
      : 'seed'

    setState(seed(source))
  }, [params, paramsRevision, seed])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // The strip offers one switch, not a mode: leaving the cloud hands the question back whatever
  // chart mode it would have shown anyway.
  const onWcToggle = useCallback(
    () => onModelChange({ chartMode: isWcChartMode(snapshot.current.state.chartMode) ? null : WC_CHART_MODE }),
    [onModelChange]
  )

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  // the chart names the question it is zooming; the block has only its own to move
  const onZoomChange = useCallback((key, zoomLevel) => onModelChange({ zoomLevel }), [onModelChange])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  const onBatchToggle = useCallback(
    (position, collapsed) => {
      const open = new Set(snapshot.current.state.uncollapsed.split('.').filter(Boolean))

      if (collapsed) open.delete(String(position))
      else open.add(String(position))

      onModelChange({ uncollapsed: [...open].sort().join('.') })
    },
    [onModelChange]
  )

  // Opening or closing every batch at once is a set operation on the batches there are, not a
  // toggle of what is currently open.
  const onToggleBatches = useCallback(
    collapse => {
      const positions = collapse
        ? []
        : calcBatches(contextRef.current.batches)
            .map(batch => String(batch.position))
            .sort()

      onModelChange({ uncollapsed: positions.join('.') })
    },
    [onModelChange]
  )

  return {
    ...state,
    isWordCloud: isWcChartMode(state.chartMode),
    modelChartMode: calcModelChartMode(state.chartMode, format),
    onChartReady,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onWcReady,
    onWcToggle,
    onBatchReady,
    onBatchStateChange,
    onBatchWcParams,
    onBatchToggle,
    onToggleBatches,
    onModelChange
  }
}
